import {
  Box,
  IconButton,
  Paper,
  Slide,
  Typography,
  makeStyles,
  createStyles,
  Theme
} from '@material-ui/core'
import { Close as CloseIcon, ChevronRight as ChevronRightIcon } from '@material-ui/icons'
import React, { useState } from 'react'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'fixed',
      bottom: theme.spacing(2),
      right: theme.spacing(2),
      zIndex: theme.zIndex.snackbar,
      maxWidth: 400,
      minWidth: 320,
      transition: 'transform 0.3s ease-in-out'
    },
    paper: {
      padding: theme.spacing(2),
      boxShadow: theme.shadows[8]
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1)
    },
    headerButtons: {
      display: 'flex',
      gap: theme.spacing(0.5)
    },
    content: {
      marginTop: theme.spacing(1)
    },
    hiddenTab: {
      position: 'fixed',
      bottom: theme.spacing(2),
      right: 0,
      zIndex: theme.zIndex.snackbar,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius,
      padding: theme.spacing(1, 0.5),
      cursor: 'pointer',
      boxShadow: theme.shadows[4],
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      }
    }
  })
)

export interface FloatingNotificationProps {
  /**
   * Título de la notificación
   */
  title?: string
  /**
   * Contenido de la notificación
   */
  children: React.ReactNode
  /**
   * Si está abierta por defecto
   */
  defaultOpen?: boolean
  /**
   * Callback cuando se cierra
   */
  onClose?: () => void
  /**
   * Permite cerrar permanentemente (vs solo ocultar)
   */
  allowClose?: boolean
}

/**
 * Componente de notificación flotante en la esquina inferior derecha
 *
 * Características:
 * - Se puede ocultar/mostrar deslizándose hacia la derecha
 * - Se puede cerrar permanentemente si allowClose=true
 * - Acepta children para contenido dinámico
 * - Posicionado en esquina inferior derecha
 *
 * @example
 * ```tsx
 * <FloatingNotification
 *   title="Procesando..."
 *   allowClose={true}
 *   onClose={() => console.log('closed')}
 * >
 *   <ProcessingProgress {...props} />
 * </FloatingNotification>
 * ```
 */
export const FloatingNotification: React.FC<FloatingNotificationProps> = ({
  title,
  children,
  defaultOpen = true,
  onClose,
  allowClose = true
}) => {
  const classes = useStyles()
  const [isVisible, setIsVisible] = useState(defaultOpen)
  const [isClosed, setIsClosed] = useState(false)

  const handleToggle = () => {
    setIsVisible(!isVisible)
  }

  const handleClose = () => {
    setIsClosed(true)
    onClose?.()
  }

  // Si fue cerrado permanentemente, no renderizar nada
  if (isClosed) {
    return null
  }

  // Si está oculto, mostrar tab lateral
  if (!isVisible) {
    return (
      <Box className={classes.hiddenTab} onClick={handleToggle}>
        <ChevronRightIcon style={{ transform: 'rotate(180deg)' }} />
      </Box>
    )
  }

  return (
    <Slide direction="left" in={isVisible} mountOnEnter unmountOnExit>
      <Box className={classes.container}>
        <Paper className={classes.paper} elevation={8}>
          <Box className={classes.header}>
            <Typography variant="subtitle1" component="h3">
              {title}
            </Typography>
            <Box className={classes.headerButtons}>
              {/* Botón para ocultar */}
              <IconButton size="small" onClick={handleToggle} aria-label="hide">
                <ChevronRightIcon />
              </IconButton>
              {/* Botón para cerrar permanentemente */}
              {allowClose && (
                <IconButton size="small" onClick={handleClose} aria-label="close">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>
          <Box className={classes.content}>{children}</Box>
        </Paper>
      </Box>
    </Slide>
  )
}
